import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, CheckCircle, Clock, Users, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

export const FCOTrainingPage = () => {
  const { language } = useLanguage();

  const practiceAreas = language === 'fr' ? [
    { name: 'Private Equity & M&A', cases: 'Due diligence automatisée, analyse de datarooms, détection de clauses de changement de contrôle, extraction automatisée de conditions suspensives' },
    { name: 'Contentieux & Arbitrage', cases: 'Recherche jurisprudentielle assistée, analyse prédictive des décisions, rédaction de conclusions, synthèse de pièces' },
    { name: 'Droit social', cases: 'Analyse de conventions collectives, comparaison de contrats de travail, veille réglementaire automatisée' },
    { name: 'Corporate & Droit des sociétés', cases: 'Rédaction de PV d\'AG, comparaison de statuts, conformité KYC/AML assistée' },
    { name: 'Droit fiscal', cases: 'Analyse de rescrits, veille fiscale automatisée, optimisation de mémos fiscaux' },
    { name: 'Droit public', cases: 'Analyse d\'appels d\'offres, vérification de conformité marchés publics, rédaction de mémoires' },
    { name: 'Droit immobilier', cases: 'Analyse de baux commerciaux, due diligence immobilière, vérification d\'urbanisme' },
    { name: 'Droit de la santé', cases: 'Conformité données de santé, analyse de protocoles, veille réglementaire ANSM/HAS' },
    { name: 'Droit de l\'urbanisme', cases: 'Analyse de PLU, vérification de permis de construire, contentieux administratif' },
    { name: 'Regulatory & Compliance', cases: 'Veille réglementaire automatisée, cartographie des obligations, analyse d\'impact' },
    { name: 'Droit pénal', cases: 'Recherche jurisprudentielle pénale, analyse de dossiers, chronologie automatisée des faits' },
  ] : [
    { name: 'Private Equity & M&A', cases: 'Automated due diligence, dataroom analysis, change of control clause detection, condition precedent extraction' },
    { name: 'Litigation & Arbitration', cases: 'AI-assisted case law research, predictive analysis, brief drafting, document synthesis' },
    { name: 'Employment law', cases: 'Collective agreement analysis, employment contract comparison, automated regulatory watch' },
    { name: 'Corporate & Company law', cases: 'Board minutes drafting, articles comparison, AI-assisted KYC/AML compliance' },
    { name: 'Tax law', cases: 'Tax ruling analysis, automated tax watch, tax memo optimization' },
    { name: 'Public law', cases: 'Tender analysis, public procurement compliance, memorial drafting' },
    { name: 'Real estate', cases: 'Commercial lease analysis, real estate due diligence, zoning verification' },
    { name: 'Healthcare law', cases: 'Health data compliance, protocol analysis, regulatory watch' },
    { name: 'Urban planning law', cases: 'Local plan analysis, building permit verification, administrative litigation' },
    { name: 'Regulatory & Compliance', cases: 'Automated regulatory watch, obligation mapping, impact analysis' },
    { name: 'Criminal law', cases: 'Criminal case law research, case file analysis, automated fact chronology' },
  ];

  const results = language === 'fr' ? [
    'Jusqu\'à 40% de gain de temps sur la recherche jurisprudentielle',
    'Rédaction de premières versions de documents 3x plus rapide',
    'Réduction des erreurs de vérification de conformité de 60%',
    'Attestation de formation continue reconnue CNB',
  ] : [
    'Up to 40% time savings on case law research',
    '3x faster first-draft document generation',
    '60% reduction in compliance verification errors',
    'CLE certificate recognized by bar associations',
  ];

  const faqs = language === 'fr' ? [
    { q: "La formation FCO est-elle éligible OPCO et FIF-PL ?", a: "Oui, tous nos modules FCO sont éligibles au financement OPCO (avocats salariés) et FIF-PL (avocats libéraux). Nous vous aidons dans les démarches administratives." },
    { q: "Combien d'heures de formation continue la FCO valide-t-elle ?", a: "Nos modules comptent de 2h à 7h de formation continue obligatoire, validées par une attestation reconnue par le CNB et les ordres." },
    { q: "Les formations sont-elles adaptées à ma matière ?", a: "Oui, chaque module est conçu pour une matière juridique spécifique avec des cas d'usage concrets issus de la pratique réelle." },
    { q: "Puis-je suivre la formation en webinaire ?", a: "Oui, tous nos modules sont disponibles en webinaire (à distance) et en présentiel. Les deux formats donnent droit à la même attestation." },
  ] : [
    { q: "Is the CLE training eligible for OPCO and FIF-PL funding?", a: "Yes, all our CLE modules are eligible for OPCO (employed lawyers) and FIF-PL (independent lawyers) funding. We assist with the administrative process." },
    { q: "How many CLE hours does the training validate?", a: "Our modules count from 2h to 7h of mandatory continuing education, validated by a certificate recognized by bar associations." },
    { q: "Are the programs tailored to my practice area?", a: "Yes, each module is designed for a specific practice area with concrete use cases from real practice." },
    { q: "Can I attend the training via webinar?", a: "Yes, all our modules are available via webinar (remote) and on-site. Both formats provide the same certification." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={language === 'fr' ? "Formation continue obligatoire IA avocat (FCO) | Gaius" : "Mandatory CLE AI Training for Lawyers | Gaius"}
        description={language === 'fr' ? "Formation continue obligatoire des avocats en IA juridique. Modules par matière : M&A, contentieux, fiscal, corporate, social, immobilier. Éligible OPCO et FIF-PL." : "Mandatory continuing legal education in legal AI. Practice-area modules: M&A, litigation, tax, corporate, employment, real estate. OPCO and FIF-PL eligible."}
        keywords={language === 'fr' ? "formation continue obligatoire avocat, fco avocat ia, formation ia avocat, opco avocat, fif-pl avocat, formation continue cnb" : "mandatory cle lawyer, cle ai training, legal ai training, opco lawyer, continuing legal education ai"}
        canonicalUrl={language === 'fr' ? "https://gaius-tech.com/formation-continue-obligatoire-avocat-ia" : "https://gaius-tech.com/mandatory-cle-ai-training-lawyers"}
        alternateUrlFr="https://gaius-tech.com/formation-continue-obligatoire-avocat-ia"
        alternateUrlEn="https://gaius-tech.com/mandatory-cle-ai-training-lawyers"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 mb-6">FCO · OPCO · FIF-PL</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {language === 'fr' ? 'Formation continue obligatoire en IA juridique' : 'Mandatory CLE in legal AI'}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            {language === 'fr'
              ? 'Modules de 2h à 7h éligibles à la formation continue obligatoire des avocats. IA appliquée par matière juridique. Financement OPCO et FIF-PL.'
              : '2h to 7h modules eligible for mandatory continuing legal education. AI applied by practice area. OPCO and FIF-PL funding available.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to={language === 'fr' ? '/formation-ia-juridique-eligible-financement' : '/legal-ai-training-eligible-funding'}>{language === 'fr' ? 'Réserver une formation' : 'Book a training'}</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to={language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'}>{language === 'fr' ? 'Voir toutes les formations' : 'View all programs'}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Clock, label: language === 'fr' ? 'Modules de 2h à 7h' : '2h to 7h modules' },
              { icon: Users, label: language === 'fr' ? 'Webinaire ou présentiel' : 'Webinar or on-site' },
              { icon: GraduationCap, label: language === 'fr' ? 'Attestation CNB' : 'CLE certificate' },
              { icon: BookOpen, label: language === 'fr' ? 'Supports inclus' : 'Materials included' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
                <f.icon className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">{f.label}</span>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-4 text-center">
            {language === 'fr' ? 'Modules par matière juridique' : 'Modules by practice area'}
          </h2>
          <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Chaque module est conçu pour une matière spécifique, avec des cas d\'usage concrets et des exercices pratiques.'
              : 'Each module is designed for a specific practice area, with concrete use cases and hands-on exercises.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {practiceAreas.map((area, i) => (
              <div key={i} className="p-5 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/20 transition-all">
                <h3 className="text-base font-semibold text-foreground mb-2">{area.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{area.cases}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            {language === 'fr' ? 'Résultats constatés' : 'Proven results'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border border-border px-6">
                <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-4">
            {language === 'fr' ? 'Réservez votre formation FCO' : 'Book your CLE training'}
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            {language === 'fr' ? 'Éligible OPCO et FIF-PL. Attestation de formation continue CNB.' : 'OPCO and FIF-PL eligible. CLE certificate included.'}
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/contact" className="flex items-center">
              {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
